/* eslint-disable import/group-exports */
import {Log} from '@aboutyou/mlm';
import {useCallback} from 'react';
import {
  useAbTestExperimentsCookieByFeatureFlag,
  useStaleAbTestExperimentsCookie,
} from './useAbTestExperimentsCookie';
import {useActivatedAbTests} from './useActivatedAbTests';
// eslint-disable-next-line no-restricted-imports, monorepo/no-internal-import
import {useExperimentActivation} from '@aboutyou/tracking/src/trackingHooks/trackExperimentActivation';
import {AvailableFeatureNames} from '../types';
import {rememberAbTestsActivatedBeforeCookieConsent} from './abTestsActivatedBeforeCookieConsent';
import {ABTests} from './types';
import {useAbTestLogger} from './useAbTestLogger';

const loggedABTests = new Set<string>();

/**
 * @deprecated Please use the new `useIsFeatureActive` hook with `abTest` config to enable this test
 */
export const useActivateAbTestByFeatureFlagCallback = (
  featureName: AvailableFeatureNames,
) => {
  const abTestExperiments = useAbTestExperimentsCookieByFeatureFlag({
    featureName,
  });
  return useBaseActivateAbTestCallback(abTestExperiments);
};

/**
 * @deprecated Please use the new `useIsFeatureActive` hook with `abTest` config to enable this test
 */
export const useActivateAnyAbTestCallback = () => {
  const abTestExperiments = useStaleAbTestExperimentsCookie();
  return useBaseActivateAbTestCallback(abTestExperiments);
};

const useBaseActivateAbTestCallback = (abTestExperiments: ABTests) => {
  const trackExperimentActivation = useExperimentActivation();
  const {logAbTestEvent} = useAbTestLogger();

  const {isExperimentActivated, setTestStateForUserId} = useActivatedAbTests();

  return useCallback(
    (
      experimentKey: string | undefined,
      config?: {triggerMultipleTimesPerSession?: boolean},
    ) => {
      // keeps track of abTests that were activated before cookie consent in-memory so that we do not rerender those after cookie consent
      rememberAbTestsActivatedBeforeCookieConsent(experimentKey);

      const abTestExperiment = abTestExperiments.find(
        ({key}) => key === experimentKey,
      );

      if (!abTestExperiment?.key || !abTestExperiment.group) {
        return;
      }

      const experiment = {
        name: abTestExperiment.key,
        value: abTestExperiment.group,
      };

      if (
        isExperimentActivated(experiment) &&
        !config?.triggerMultipleTimesPerSession
      ) {
        /**
         * isExperimentActivated checks in `LocalStorage` if the AB Test was already activated for the user, so that we don't activate the AB Test again for the same user.
         * But the attribute we sent to NR Browser needs to happen for returning users as well, in every session. This is why we are storing that in a simple global variable `loggedABTests`.
         */
        if (!loggedABTests.has(experiment.name)) {
          setAbTestCustomAttributes(experiment);
        }
        return;
      }

      // `pending` is an in-memory marker; the localStorage write behind
      // `activated` resolves asynchronously, so without it a second activation
      // in the same tick would count as a new exposure
      setTestStateForUserId(experiment, 'pending');
      setTestStateForUserId(experiment, 'activated');
      trackExperimentActivation(experiment);
      logAbTestEvent({
        event: 'activation',
        attributes: {
          ab_test_key: experiment.name,
          ab_test_group: experiment.value,
        },
      });
      setAbTestCustomAttributes(experiment);
    },
    [
      abTestExperiments,
      isExperimentActivated,
      logAbTestEvent,
      setTestStateForUserId,
      trackExperimentActivation,
    ],
  );
};

const setAbTestCustomAttributes = (experiment: {
  name: string;
  value: string;
}) => {
  Log.setCustomAttribute(`AB_test_name`, experiment.name);
  Log.setCustomAttribute(`AB_test_value`, experiment.value);
  loggedABTests.add(experiment.name);
};
